import { useMessagePlacementHooks } from '@atlassian/post-office-context';
import { createPlacementComponent } from '@post-office/placement-common/utils/create-placement-component';
import {
	MaybeSuspenseBoundary,
	type MessageProps,
} from '@post-office/placement-common/utils/message-renderer';
import { useInitialData } from '@post-office/placement-contexts/hooks';
import { memo, useCallback, useEffect, useState } from 'react';
import FeatureGates from '@atlaskit/feature-gate-js-client';

import { MessageRenderer, type renderer } from '../__generated__/client/render';
import { PLACEMENT_ID } from '../__generated__/common';
import { type JiraSideNavInitialData } from '../api/types';
import { type Placement as JiraSideNavPlacement } from '../contracts/Placement';

export type Message = MessageProps<typeof renderer>;

const excludedMessages = new Set(['test-message-inapp', 'message-template-template']);

export const JiraSideNavPlacementComponent = createPlacementComponent<JiraSideNavPlacement>(
	PLACEMENT_ID,
	memo(({ initialData, preloadedInitialData, handleClearPreloadedData }) => {
		const { data, isLoading } = useInitialData<JiraSideNavInitialData>(initialData);
		const [{ message, status }, setMessageState] = useState<{
			message: Message | undefined;
			status: 'loading' | 'done';
		}>(() => {
			if (preloadedInitialData) {
				return preloadedInitialData?.messages?.length > 0
					? {
							message: preloadedInitialData?.messages[0] as Message,
							status: 'done',
						}
					: { message: undefined, status: 'done' };
			}

			return { message: undefined, status: 'loading' };
		});

		const handleOnDismissed = useCallback(() => {
			setMessageState({ message: undefined, status: 'done' });

			if (typeof handleClearPreloadedData === 'function') {
				handleClearPreloadedData();
			}
		}, [handleClearPreloadedData]);

		const { onMessageDismiss, onMessageClicked, onMessageSnooze } = useMessagePlacementHooks();

		useEffect(() => onMessageDismiss(handleOnDismissed), [onMessageDismiss, handleOnDismissed]);
		useEffect(() => onMessageClicked(handleOnDismissed), [onMessageClicked, handleOnDismissed]);
		useEffect(() => onMessageSnooze(handleOnDismissed), [onMessageSnooze, handleOnDismissed]);

		const isDone = status === 'done';

		useEffect(() => {
			if (!isLoading && !isDone) {
				const filteredMessages = ((data?.messages || []) as Message[]).filter((dataMessage) => {
					if (excludedMessages.has(dataMessage.messageTemplateId)) {
						return false;
					}

					if (dataMessage.messageTemplateId === 'rovo-extension-install-cta-left-nav') {
						const cohort = FeatureGates.getExperimentValue<string>(
							'rovo-ext_left_nav_rovo_extension_cta',
							'cohort',
							'not-enrolled',
							{ fireExperimentExposure: true },
						);

						if (cohort === 'not-enrolled' || cohort === 'control') {
							return false;
						}
					}

					return true;
				});

				const newMessage = filteredMessages[0];

				setMessageState({
					message: newMessage,
					status: 'done',
				});
			}
		}, [data, isLoading, isDone]);

		if (status === 'loading') {
			return null;
		}

		return (
			message && (
				<MessageRenderer
					{...message}
					id={message.messageInstanceId}
					onDismissed={handleOnDismissed}
					SuspenseBoundary={MaybeSuspenseBoundary}
				/>
			)
		);
	}),
	{
		choreographer: {
			isDefaultEnabled: false,
		},
	},
);

export default JiraSideNavPlacementComponent;
